import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  addCaseNote,
  blockCase,
  CaseNote,
  CaseSeverity,
  CaseStatus,
  claimCase,
  closeCase,
  escalateCase,
  flagCase,
  getCaseNotes,
  getHandoverDraft,
  getOpsQueue,
  HandoverDraft,
  OpsCase,
  OpsQueue,
  reassignCase,
  releaseCase,
  resumeCase,
  sendHandover,
} from '../api/ops-console';
import {
  Drawer,
  Empty,
  Field,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  Section,
  SlaTag,
  Steps,
  Table,
  Value,
} from '../../../design-system/ops';
import { PLATFORM_CURRENCY, formatMoney } from '../../../commodity/format/money';
import { UTC_ZONE, formatShortDateTime } from '../time/zonedTime';

/**
 * "My queues" — UC-A02, the console's Today screen.
 *
 * Everything on it comes from a persisted record. The prototype fills this
 * screen from an invented `CASES` array, and porting that would have put nine
 * plausible cases on an operations console that no queue produced — which is
 * the most expensive kind of misunderstanding a demo can create. What renders
 * here is what the case sources actually report.
 */

const SEVERITY_TONE: Record<CaseSeverity, PillTone> = {
  CRITICAL: 'bad',
  HIGH: 'bad',
  MEDIUM: 'warn',
  LOW: 'neu',
};

const SEVERITY_LABEL: Record<CaseSeverity, string> = {
  CRITICAL: 'Crítica',
  HIGH: 'Alta',
  MEDIUM: 'Media',
  LOW: 'Baja',
};

const STATUS_LABEL: Record<CaseStatus, string> = {
  OPEN_UNASSIGNED: 'Sin asignar',
  IN_PROGRESS: 'En curso',
  BLOCKED: 'Bloqueado',
  ESCALATED: 'Escalado',
  CLOSED: 'Cerrado',
};

const CLOSE_REASON_LABEL: Record<string, string> = {
  DUPLICATE_MERGED: 'Duplicado',
  CANCELLED_CONDITION: 'La condición se canceló',
  SUPERSEDED: 'Reemplazado por otro caso',
};

const COLUMNS = '150px 1fr 120px 96px 120px';

/** Which form the case drawer has open. One at a time. */
type CasePanel = 'escalate' | 'block' | 'close' | 'reassign';

const EMPTY_FORM = {
  reason: '',
  target: '',
  note: '',
  nextCheckAt: '',
  closeReason: '',
  toOperatorId: '',
};

const PANEL_LABEL: Record<CasePanel, string> = {
  escalate: 'Escalar',
  block: 'Bloquear',
  reassign: 'Reasignar',
  close: 'Cerrar',
};

const AdminCaseQueue: React.FC = () => {
  const [queue, setQueue] = useState<OpsQueue | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Which case is open, in the URL.
   *
   * ADM-01-FR-06: *"Returning from a case retains the originating queue
   * state."* Keeping it here rather than in component state is what makes that
   * true for free — the browser's own back button restores the queue exactly as
   * it was, and a link to a case is a link someone can send to a colleague.
   */
  const [params, setParams] = useSearchParams();
  const openCaseId = params.get('case');

  const [notes, setNotes] = useState<CaseNote[]>([]);
  const [notesFailed, setNotesFailed] = useState(false);
  const [draftNote, setDraftNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const [panel, setPanel] = useState<CasePanel | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const setField = (field: keyof typeof EMPTY_FORM, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const [handover, setHandover] = useState<HandoverDraft | null>(null);
  const [handoverNote, setHandoverNote] = useState('');
  const [toShift, setToShift] = useState('');
  const [handoverFailed, setHandoverFailed] = useState<string | null>(null);
  const [handoverResult, setHandoverResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setQueue(await getOpsQueue());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar la cola');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = queue?.cases.find((c) => c.caseId === openCaseId);

  useEffect(() => {
    setNotesFailed(false);
    if (!openCaseId) {
      setNotes([]);
      return;
    }
    getCaseNotes(openCaseId)
      .then((r) => setNotes(r.notes))
      .catch(() => {
        // Not the same as a case with no notes, so it does not look like one.
        setNotes([]);
        setNotesFailed(true);
      });
  }, [openCaseId]);

  const closeDrawer = () => {
    params.delete('case');
    setParams(params, { replace: true });
    setDraftNote('');
    setFlash(null);
    setPanel(null);
    setForm(EMPTY_FORM);
  };

  const openHandover = async () => {
    setHandoverFailed(null);
    setHandoverResult(null);
    try {
      const draft = await getHandoverDraft();
      setHandover(draft);
      setHandoverNote(draft.draft);
    } catch (e: any) {
      setHandoverFailed(e?.message || 'No se pudo generar la nota de relevo');
    }
  };

  const submitHandover = () =>
    act(async () => {
      const result = await sendHandover({
        note: handoverNote,
        toShift: toShift.trim(),
        generatedAt: handover?.generatedAt ?? '',
        draftCaseIds: (handover?.caseRefs ?? []).map((r) => r.caseId),
      });
      setHandover(null);
      setHandoverResult(
        result.addedAtFinalize.length
          ? `Relevo enviado. Se añadieron ${result.addedAtFinalize.length} caso(s) nuevos al finalizar.`
          : 'Relevo enviado.',
      );
    }, 'Relevo enviado');

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setFlash(null);
    try {
      await fn();
      setFlash(done);
      setPanel(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (e: any) {
      // The claim race returns the operator who won. Showing that message
      // verbatim is the point — "held by op-2" tells the loser what to do
      // next, and a generic failure does not. The reload is the refresh
      // DEC-A02-09 promises the loser: the next attempt carries the new version.
      setFlash(e?.message || 'No se pudo completar la acción');
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <OpsPage title="Mis colas">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!queue) {
    return (
      <OpsPage title="Mis colas">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const money = queue.cases.reduce((sum, c) => sum + (c.amountUsd ?? 0), 0);
  const windowHours = queue.dueSoonWindowHours;

  const block = (title: string, cases: OpsCase[], subtitle?: string) =>
    cases.length ? (
      <Section title={title} count={cases.length} subtitle={subtitle} key={title}>
        <Table columns={COLUMNS} head={['Caso', 'Asunto', 'Familia', 'Severidad', 'SLA']}>
          {cases.map((c) => (
            <Row
              key={c.caseId}
              onOpen={() => {
                params.set('case', c.caseId);
                setParams(params);
              }}
              label={`Abrir ${c.title}`}
            >
              <span className="mut">{c.sourceId}</span>
              <span>
                <b>{c.title}</b>
                <br />
                <span className="mut">{c.subtitle}</span>
              </span>
              <span>
                {c.family ?? '—'}
                {c.owner ? (
                  <>
                    <br />
                    <Pill tone="tint">{c.owner.operatorId}</Pill>
                  </>
                ) : null}
              </span>
              <Pill tone={SEVERITY_TONE[c.severity]}>{SEVERITY_LABEL[c.severity]}</Pill>
              <SlaTag hoursRemaining={c.sla.hoursRemaining} dueSoonHours={c.sla.dueSoonHours} />
            </Row>
          ))}
        </Table>
      </Section>
    ) : null;

  return (
    <OpsPage
      title="Mis colas"
      subtitle="Todo lo que espera a Operaciones hoy."
      action={
        <button className="btn btn-o" onClick={openHandover}>
          Nota de relevo
        </button>
      }
    >
      <KpiRow>
        <Kpi
          label="Críticos"
          value={queue.critical.length}
          note={queue.critical.length ? 'Primero estos' : 'Ninguno'}
          tone={queue.critical.length ? 'bad' : 'neutral'}
          alert={queue.critical.length > 0}
        />
        <Kpi
          label="Incumpliendo SLA"
          value={queue.breaching.length}
          note={queue.breaching.length ? 'Actuar ahora' : 'Nada vencido'}
          tone={queue.breaching.length ? 'bad' : 'neutral'}
          alert={queue.breaching.length > 0}
        />
        <Kpi
          label={`Vencen en ${windowHours}h`}
          value={queue.dueSoon.length}
          note="Siguiente"
          tone={queue.dueSoon.length ? 'warn' : 'neutral'}
        />
        <Kpi label="Casos abiertos" value={queue.cases.length} note="En total" />
        <Kpi
          label="Dinero congelado"
          value={formatMoney(money, PLATFORM_CURRENCY)}
          note="En escrow detenido"
          tone={money > 0 ? 'lav' : 'neutral'}
        />
      </KpiRow>

      {/* A rail that could not be read is named, never silently empty: an
          operator who cannot see it assumes there is nothing there. */}
      {queue.gaps.length ? (
        <Note tone="warn">
          Esta vista está incompleta.{' '}
          {queue.gaps.map((g) => `${g.module}: ${g.reason}`).join(' · ')}
        </Note>
      ) : null}

      {handoverFailed ? <Note tone="bad">{handoverFailed}</Note> : null}
      {handoverResult ? <Note>{handoverResult}</Note> : null}
      {openCaseId && !open ? (
        <Note tone="warn">Este caso ya no está abierto, o no tienes acceso a él.</Note>
      ) : null}

      {queue.cases.length === 0 ? (
        <Empty title="Nada abierto" detail="Todas las colas están limpias." />
      ) : (
        <>
          {block(
            '⛔ Críticos',
            queue.critical,
            'Van primero, sea cual sea su plazo. La severidad la decide el registro de origen.',
          )}
          {block(
            '🚨 Incumpliendo SLA',
            queue.breaching,
            'Rompen una promesa a una familia o a un profesor. Atender primero.',
          )}
          {block(`⏳ Vencen en las próximas ${windowHours} horas`, queue.dueSoon)}
          {block('📋 Programado', queue.scheduled)}
        </>
      )}

      {queue.caseTypesWithoutSource.length ? (
        <Note>Tipos de caso aún sin fuente: {queue.caseTypesWithoutSource.join(', ')}.</Note>
      ) : null}

      <Drawer
        open={Boolean(open)}
        onClose={closeDrawer}
        title={open?.title ?? ''}
        meta={open ? `${open.caseId} · ${open.subtitle}` : undefined}
        footer={
          open ? (
            <>
              <button
                className="btn btn-o"
                disabled={busy}
                aria-pressed={open.flagged}
                onClick={() =>
                  act(
                    () => flagCase(open.caseId, !open.flagged),
                    open.flagged ? 'Quitado del relevo' : 'Marcado para el relevo',
                  )
                }
              >
                {open.flagged ? 'Quitar del relevo' : 'Marcar para el relevo'}
              </button>
              {open.owner ? (
                <>
                  {(['escalate', 'block', 'reassign', 'close'] as CasePanel[]).map((p) => (
                    <button
                      key={p}
                      className="btn btn-o"
                      disabled={busy}
                      aria-expanded={panel === p}
                      onClick={() => setPanel(panel === p ? null : p)}
                    >
                      {PANEL_LABEL[p]}
                    </button>
                  ))}
                  {open.status === 'BLOCKED' || open.status === 'ESCALATED' ? (
                    <button
                      className="btn btn-o"
                      disabled={busy}
                      onClick={() => act(() => resumeCase(open.caseId), 'Caso retomado')}
                    >
                      Retomar
                    </button>
                  ) : null}
                  {open.status === 'IN_PROGRESS' ? (
                    <button
                      className="btn btn-o"
                      disabled={busy}
                      onClick={() =>
                        act(() => releaseCase(open.caseId, open.ownerVersion), 'Devuelto a la cola')
                      }
                    >
                      Devolver a la cola
                    </button>
                  ) : null}
                </>
              ) : (
                <button
                  className="btn btn-p"
                  disabled={busy}
                  onClick={() =>
                    act(() => claimCase(open.caseId, open.ownerVersion), 'Caso asignado')
                  }
                >
                  Asignármelo
                </button>
              )}
            </>
          ) : null
        }
      >
        {open ? (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Pill tone={SEVERITY_TONE[open.severity]}>{SEVERITY_LABEL[open.severity]}</Pill>
              <Pill tone="neu">{STATUS_LABEL[open.status]}</Pill>
              <SlaTag
                hoursRemaining={open.sla.hoursRemaining}
                dueSoonHours={open.sla.dueSoonHours}
              />
              {open.amountUsd ? (
                <Pill tone="lav">{formatMoney(open.amountUsd, PLATFORM_CURRENCY)} congelados</Pill>
              ) : null}
            </div>

            {flash ? <Note tone="warn">{flash}</Note> : null}

            {panel === 'escalate' ? (
              <Field label="Escalar a una cola">
                <select
                  className="inp"
                  aria-label="Motivo de escalamiento"
                  value={form.reason}
                  onChange={(e) => setField('reason', e.target.value)}
                >
                  <option value="">Elige el motivo</option>
                  {queue.escalationReasons.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <select
                  className="inp"
                  aria-label="Cola de destino"
                  value={form.target}
                  onChange={(e) => setField('target', e.target.value)}
                >
                  <option value="">Elige la cola</option>
                  {queue.escalationTargets.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <textarea
                  className="inp"
                  rows={3}
                  aria-label="Por qué se escala"
                  value={form.note}
                  onChange={(e) => setField('note', e.target.value)}
                />
                <button
                  className="btn btn-p btn-xs"
                  disabled={busy || !form.reason || !form.target || form.note.trim().length < 8}
                  onClick={() =>
                    act(async () => {
                      const result = await escalateCase(open.caseId, {
                        reason: form.reason,
                        target: form.target,
                        note: form.note,
                      });
                      if (result.targetUnavailable) {
                        throw new Error(
                          `${result.toQueue} no tiene a nadie disponible. El caso sigue contigo y quedó bloqueado.`,
                        );
                      }
                    }, 'Escalado')
                  }
                >
                  Confirmar escalamiento
                </button>
              </Field>
            ) : null}

            {panel === 'block' ? (
              <Field label="Bloquear">
                <textarea
                  className="inp"
                  rows={2}
                  aria-label="Por qué está bloqueado"
                  value={form.note}
                  onChange={(e) => setField('note', e.target.value)}
                />
                <input
                  className="inp"
                  type="datetime-local"
                  aria-label="Próxima revisión"
                  value={form.nextCheckAt}
                  onChange={(e) => setField('nextCheckAt', e.target.value)}
                />
                <button
                  className="btn btn-p btn-xs"
                  disabled={busy || form.note.trim().length < 8 || !form.nextCheckAt}
                  onClick={() =>
                    act(
                      () =>
                        blockCase(open.caseId, {
                          blockReason: form.note,
                          nextCheckAt: new Date(form.nextCheckAt).toISOString(),
                        }),
                      'Caso bloqueado',
                    )
                  }
                >
                  Confirmar bloqueo
                </button>
              </Field>
            ) : null}

            {panel === 'reassign' ? (
              <Field label="Reasignar">
                <input
                  className="inp"
                  aria-label="Operador que lo recibe"
                  value={form.toOperatorId}
                  onChange={(e) => setField('toOperatorId', e.target.value)}
                />
                <textarea
                  className="inp"
                  rows={2}
                  aria-label="Por qué se reasigna"
                  value={form.note}
                  onChange={(e) => setField('note', e.target.value)}
                />
                <button
                  className="btn btn-p btn-xs"
                  disabled={busy || !form.toOperatorId.trim() || form.note.trim().length < 8}
                  onClick={() =>
                    act(
                      () =>
                        reassignCase(open.caseId, {
                          toOperatorId: form.toOperatorId.trim(),
                          reason: form.note,
                          expectedOwnerVersion: open.ownerVersion,
                        }),
                      'Caso reasignado',
                    )
                  }
                >
                  Confirmar reasignación
                </button>
              </Field>
            ) : null}

            {panel === 'close' ? (
              <Field label="Cerrar">
                <select
                  className="inp"
                  aria-label="Motivo de cierre"
                  value={form.closeReason}
                  onChange={(e) => setField('closeReason', e.target.value)}
                >
                  <option value="">Elige el motivo</option>
                  {queue.closeReasons.map((r) => (
                    <option key={r} value={r}>
                      {CLOSE_REASON_LABEL[r] ?? r}
                    </option>
                  ))}
                </select>
                <textarea
                  className="inp"
                  rows={2}
                  aria-label="Por qué se cierra"
                  value={form.note}
                  onChange={(e) => setField('note', e.target.value)}
                />
                <button
                  className="btn btn-p btn-xs"
                  disabled={busy || !form.closeReason || form.note.trim().length < 8}
                  onClick={() =>
                    act(
                      () =>
                        closeCase(open.caseId, {
                          closeReason: form.closeReason,
                          note: form.note,
                        }),
                      'Caso cerrado',
                    )
                  }
                >
                  Confirmar cierre
                </button>
              </Field>
            ) : null}

            {open.sourceUnavailable ? (
              <Note tone="warn">
                El registro de origen no respondió. Esto es lo guardado en el caso.
              </Note>
            ) : null}

            <Field label="Por qué importa">
              <Note
                tone={open.severity === 'CRITICAL' || open.severity === 'HIGH' ? 'bad' : 'warn'}
              >
                {open.why}
              </Note>
            </Field>

            {open.family ? (
              <Field label="Familia">
                <Value>{open.family}</Value>
              </Field>
            ) : null}

            <Field label="Qué hacer">
              <Steps steps={open.nextActions} />
            </Field>

            <Field label="Responsable">
              <Value>
                {open.owner ? (
                  <Pill tone="tint">{open.owner.operatorId}</Pill>
                ) : (
                  <span className="mut">Sin asignar — en la cola compartida</span>
                )}
              </Value>
            </Field>

            {open.status === 'BLOCKED' ? (
              <Field label="Bloqueo">
                <Note tone="warn">
                  {open.blockReason}
                  {open.nextCheckAt
                    ? ` · próxima revisión ${formatShortDateTime(open.nextCheckAt, UTC_ZONE)} UTC`
                    : ''}
                </Note>
              </Field>
            ) : null}

            {open.escalation ? (
              <Field label="Escalado">
                <Note tone="warn">
                  A {open.escalation.toQueue} por {open.escalation.escalatedBy}:{' '}
                  {open.escalation.note ?? open.escalation.reason}
                  {open.escalation.targetUnavailable
                    ? ` · sin nadie disponible${
                        open.escalation.fallbackQueue
                          ? `; enviado a ${open.escalation.fallbackQueue}`
                          : ''
                      }`
                    : ''}
                </Note>
              </Field>
            ) : null}

            {open.playbook ? (
              <Field label="Playbook">
                <Value>
                  <a href="/admin/playbook">
                    {open.playbook.id} v{open.playbook.version}
                  </a>
                </Value>
              </Field>
            ) : null}

            <Field label="Registro autoritativo">
              <Value>
                <a href={open.sourceRoute}>{open.sourceRoute}</a>
              </Value>
            </Field>

            <Field label={notesFailed ? 'Notas internas' : `Notas internas (${notes.length})`}>
              {notesFailed ? (
                <Note tone="bad">No se pudieron cargar las notas de este caso.</Note>
              ) : null}
              {/* Never reach a family. The service that stores these has no
                  method that can send anything, so the guarantee is structural
                  rather than a checkbox someone might miss. */}
              {notes.map((n) => (
                <Note key={n.noteId}>
                  <b>{n.authorId}</b> · {new Date(n.createdAt).toLocaleString('es-MX')}
                  <br />
                  {n.body}
                </Note>
              ))}
              <textarea
                id={`case-note-${open.caseId}`}
                className="inp"
                rows={3}
                value={draftNote}
                placeholder="¿Qué hiciste? Sólo lo ve Operaciones."
                aria-label="Añadir una nota interna"
                onChange={(e) => setDraftNote(e.target.value)}
              />
              <button
                className="btn btn-o btn-xs"
                disabled={busy || !draftNote.trim()}
                onClick={() =>
                  act(async () => {
                    await addCaseNote(open.caseId, draftNote);
                    setDraftNote('');
                    setNotes((await getCaseNotes(open.caseId)).notes);
                  }, 'Nota guardada')
                }
              >
                Guardar nota
              </button>
            </Field>
          </>
        ) : null}
      </Drawer>

      <Drawer
        open={handover !== null}
        onClose={() => setHandover(null)}
        title="Nota de relevo"
        meta="Generada desde los registros de este momento"
        footer={
          <button
            className="btn btn-p"
            disabled={busy || toShift.trim().length < 2}
            onClick={submitHandover}
          >
            Enviar al siguiente turno
          </button>
        }
      >
        <Note>
          Edita lo que quieras antes de enviar. El siguiente turno arranca desde esta nota.
        </Note>
        <textarea
          id="handover-note"
          className="inp"
          rows={20}
          style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.6 }}
          value={handoverNote}
          aria-label="Nota de relevo"
          onChange={(e) => setHandoverNote(e.target.value)}
        />
        <input
          className="inp"
          value={toShift}
          placeholder="Turno que recibe"
          aria-label="Turno que recibe"
          onChange={(e) => setToShift(e.target.value)}
        />
      </Drawer>
    </OpsPage>
  );
};

export default AdminCaseQueue;
